<template>
    <div class="container mx-auto px-4">
        <h1 class="font-serif mb-8 md:text-5xl text-2xl text-center">
            {{ __('Rendelések') }}
        </h1>

        <nav class="flex mb-8">
            <router-link class="bg-white border border-slate-200 cursor-pointer flex flex-col items-center justify-center md:flex-row -mr-px p-2 rounded-bl rounded-tl text-center text-slate-800 text-sm"
                         v-bind:to="{ name : 'orders.table' }"
            >
                <c-icon class="h-6 md:mr-2 stroke-slate-800 w-6" icon="columns" />
                Táblázatos nézet
            </router-link>
            <router-link class="bg-white border border-slate-200 cursor-pointer flex flex-col items-center justify-center md:flex-row -mr-px p-2 text-center text-slate-800 text-sm"
                         v-bind:to="{ name : 'orders.daily' }"
            >
                <c-icon class="h-6 md:mr-2 stroke-slate-800 w-6" icon="users" />
                Napi bontás
            </router-link>
            <router-link class="bg-white border border-slate-200 cursor-pointer flex flex-col items-center justify-center md:flex-row -mr-px p-2 text-center text-slate-800 text-sm"
                         v-bind:to="{ name : 'orders.byuser' }"
            >
                <c-icon class="h-6 md:mr-2 stroke-slate-800 w-6" icon="user" />
                Megrendelő szerint
            </router-link>
            <span class="bg-highlight border border-slate-200 cursor-pointer flex flex-col items-center justify-center md:flex-row p-2 rounded-br rounded-tr text-center text-sm text-white">
                <c-icon class="h-6 md:mr-2 stroke-white w-6" icon="clipboard" />
                Termék összesítő
            </span>
        </nav>

        <div class="flex items-center justify-end mb-8">
            <c-button class="bg-slate-800 ml-4 shrink-0 text-white"
                      router
                      v-bind:disabled="!products || Object.keys(products).length === 0"
                      v-on:click="exportXls"
            >
                <c-icon class="h-4 mr-2 stroke-white w-4" icon="download" />{{ __('Exportálás') }}
            </c-button>
        </div>

        <div class="bg-highlight flex items-center justify-between mb-10 px-2 py-4 text-white">
            <a class="cursor-pointer flex items-center text-lg text-white uppercase"
               v-on:click="prevDay"
            >
                <c-icon class="h-6 mr-2 stroke-white w-6" icon="chevron-left" />
                <span class="hidden md:inline">{{ __('Előző') }}</span>
            </a>
            <span class="font-serif text-3xl text-center">
                {{ DateTime.fromISO(currentDate).toFormat(localizationStore.dateFormat) }}
                <small class="capitalize font-sans text-base">
                    ({{ DateTime.fromISO(currentDate).setLocale(localizationStore.locale).toFormat('cccc') }})
                </small>
            </span>
            <a class="cursor-pointer flex items-center text-lg text-white uppercase"
               v-on:click="nextDay"
            >
                <span class="hidden md:inline">{{ __('Következő') }}</span>
                <c-icon class="h-6 ml-2 stroke-white w-6" icon="chevron-right" />
            </a>
        </div>

        <div class="mt-10" v-if="products && Object.keys(products).length > 0">
            <div class="bg-white border-b border-slate-200 flex items-center last:border-b-0 p-2" v-for="product in products">
                <strong class="mr-4">{{ product.quantity }}x</strong>
                <div class="flex items-center justify-between w-full">
                    <div class="mr-4">
                        <strong class="block text-slate-800 text-sm">{{ product.product.name }}</strong>
                        <span class="block max-w-96 text-slate-500 text-xs">{{ product.product.description }}</span>
                    </div>
                    <img class="max-w-32"
                         v-bind:alt="product.product.name"
                         v-bind:src="product.product.picture"
                    />
                </div>
            </div>
        </div>

        <span class="block font-serif text-center text-2xl" v-else>{{ __('Nincs találat') }}</span>
    </div>
</template>

<script setup lang="ts">
    import { inject, onMounted, ref } from 'vue';
    import {
        CButton,
        CIcon,
    } from '@teamfurther/cinderblock';
    import { DateTime } from 'luxon';
    import OrderRepository from '../../repositories/OrderRepository';
    import { useLocalizationStore } from '../../stores/localization';
    import { useRequestStore } from '../../stores/request';
    import { useSessionStore } from '../../stores/session';

    const __ = inject('__');
    const currentDate = ref(DateTime.now().setLocale('en'));
    const localizationStore = useLocalizationStore();
    const orderRepository = new OrderRepository();
    const products = ref();
    const requestStore = useRequestStore();
    const sessionStore = useSessionStore();
    const user = ref(sessionStore.user);

    async function exportXls() {
        requestStore.post('orders/export/byproduct', {
            date: currentDate.value.toFormat('yyyy-MM-dd'),
        }).then((response : any) => {
            const blob = new Blob([
                new Uint8Array([0xEF, 0xBB, 0xBF]), // UTF-8 BOM
                response.data
            ]);
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.style.display = 'none';
            a.href = url;

            a.download = 'termek_'+ currentDate.value.toFormat('yyyy-MM-dd') +'.csv';
            document.body.appendChild(a);
            a.click();
            window.URL.revokeObjectURL(url);
        });
    }

    async function getOrders() {
        const result = await orderRepository.index({
            filters: {
                day: currentDate.value.weekdayLong.toLowerCase(),
                week: currentDate.value.year + '.' + currentDate.value.weekNumber,
            },
            include: ['items', 'items.product', 'user', 'user.group'],
        });

        products.value = {};
        result.forEach((order) => {
            order.items.forEach((item) => {
                if (item.day === currentDate.value.weekdayLong.toLowerCase()) {
                    if (!products.value[item.product_id]) {
                        products.value[item.product_id] = {
                            product: item.product,
                            quantity: item.quantity
                        }
                    } else {
                        products.value[item.product_id].quantity += item.quantity;
                    }
                }
            });
        });
    }

    async function nextDay() {
        currentDate.value = currentDate.value.plus({ days: 1 });

        if (currentDate.value.weekday === 6) {
            currentDate.value = currentDate.value.plus({ day: 2 });
        }

        await getOrders();
    }

    async function prevDay() {
        currentDate.value = currentDate.value.minus({ days: 1 });

        if (currentDate.value.weekday === 7) {
            currentDate.value = currentDate.value.minus({ day: 2 });
        }

        await getOrders();
    }

    onMounted(async () => {
        await getOrders();
    });
</script>
